import { useCallback, useMemo } from "react";
import axios from "axios";
import { useUserContext } from "../../Contexts/UserProvider";
import { useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "../../Authentication/authConfig.js";
import { acquireAccessToken } from "../../Authentication/authToken.js";

import { APP_CONSTANTS } from "../../constants/appConstants";

const API_KEY = APP_CONSTANTS.API.API_KEY;
const BASE_URL = APP_CONSTANTS.API.BASE_URL;

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: string | null) => void; reject: (reason?: any) => void; }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const useApi = () => {
  const { user } = useUserContext();
  const { id } = useParams();
  const { accounts, instance } = useMsal();

  /** Refresh or acquire latest token */
  const refreshToken = useCallback(async () => {
    if (!accounts?.length) return null;

    try {
      const response = await acquireAccessToken(instance, accounts[0]);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        if (!sessionStorage.getItem("msal_redirect_guard")) {
          sessionStorage.setItem("msal_redirect_guard", "1");
          console.warn("Token interaction required — redirecting once to sign in");
          instance.loginRedirect(loginRequest);
        }
      } else {
        console.error("Token refresh error:", error);
      }
      return null;
    }
  }, [accounts, instance]);

  /** Base Axios instance (safe to memoize, no headers here) */
  const baseAxios = useMemo(() => {
    return axios.create({
      baseURL: BASE_URL,
    });
  }, []);

  /** Helper to attach latest headers before sending */
  const withAuthHeaders = useCallback(
    async (customHeaders?: Record<string, string>, userContext?: any) => {
      const token = await refreshToken();

      // Selected tenant only: user context -> route :id -> persisted selection.
      // Never invent a default tenant code.
      const currentEmail = userContext?.email || user?.email || localStorage.getItem("email");
      const currentTenant =
        userContext?.tenant ||
        user?.tenant ||
        id ||
        localStorage.getItem("tenantCode");

      console.log("userContext : ", user);
      const x_user = currentEmail;
      const x_tenant = currentTenant;

      return {
        "Ocp-Apim-Subscription-Key": API_KEY,
        "X-User": x_user,
        "X-Tenant-Code": x_tenant,
        Authorization: `Bearer ${token}`,
        ...customHeaders,
      };
    },
    [user, id, refreshToken]
  );

  /** Construct absolute URI */
  const getURI = useCallback((uri: string) => `${BASE_URL}${uri}`, []);

  /** Core request builder — used by GET/POST/PUT/DELETE */
  const request = useCallback(
    async (method: string, uri: string, data?: any, params?: any, headers?: any) => {
      const config = {
        method,
        url: getURI(uri),
        data,
        params,
      };

      try {
        const reqHeaders = await withAuthHeaders(headers, user);
        const response = await baseAxios.request({ ...config, headers: reqHeaders });
        return response.data;
      } catch (error: any) {
        if (error?.response?.status === 401) {
          if (isRefreshing) {
            try {
              const newToken = await new Promise<string | null>((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              });
              if (newToken) {
                const retryHeaders = { ...(await withAuthHeaders(headers, user)), Authorization: `Bearer ${newToken}` };
                const retryResponse = await baseAxios.request({ ...config, headers: retryHeaders });
                return retryResponse.data;
              }
            } catch (err) {
              return err;
            }
          }

          isRefreshing = true;

          try {
            console.warn("401 detected — locking and refreshing token");
            const freshToken = await refreshToken();

            if (freshToken) {
              processQueue(null, freshToken);
              const retryHeaders = { ...(await withAuthHeaders(headers, user)), Authorization: `Bearer ${freshToken}` };
              const retryResponse = await baseAxios.request({ ...config, headers: retryHeaders });
              return retryResponse.data;
            } else {
              processQueue(new Error("Refresh failed"));
              return error;
            }
          } catch (retryError) {
            processQueue(retryError);
            console.error("Retry failed:", retryError);
            return retryError;
          } finally {
            isRefreshing = false;
          }
        }

        console.error(`${method} Error:`, error);
        return error;
      }
    },
    [baseAxios, getURI, withAuthHeaders, refreshToken, user]
  );

  /** Exposed helper methods */
  const GET = useCallback((uri: string, params?: any) => request("GET", uri, null, params), [request]);
  const POST = useCallback((uri: string, data?: any) => request("POST", uri, data), [request]);
  const PUT = useCallback((uri: string, data?: any) => request("PUT", uri, data), [request]);
  const DELETE = useCallback((uri: string) => request("DELETE", uri), [request]);

  /** For special cases with custom headers (global/public) */
  const dynamicRequest = useCallback(
    async (uri, data, x_user?, x_tenant?) => {
      const headers = {
        "X-User": x_user || (user?.isGlobalUser ? user?.email : user?.objectId),
        "X-Tenant-Code": x_tenant || id || APP_CONSTANTS.API.DEFAULT_TENANT_ID,
      };
      return request("POST", uri, data, null, headers);
    },
    [user, id, request]
  );

  const globleRequest = useCallback(
    async (uri, x_user, x_objectId, x_tenant, x_token) => {
      const headers = {
        "X-User": x_user || x_objectId,
        "X-Tenant-Code": x_tenant,
        "Ocp-Apim-Subscription-Key": API_KEY,
        Authorization: `Bearer ${x_token}`,
      };
      const response = await axios.get(uri, { headers });
      return response.data;
    },
    []
  );

  const publicRequest = useCallback(async (uri: string) => {
    const response = await baseAxios.get(getURI(uri));
    return response.data;
  }, [baseAxios, getURI]);

  return { GET, POST, PUT, DELETE, dynamicRequest, publicRequest, globleRequest, refreshToken };
};

export default useApi;
